import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, WidgetValues, ColoringMode, IValueViewModel, AlarmLevel, Alarm } from '../../models';
import { GaugeOptions, GaugeDataFormat } from '../../framework/charting/charts/Gauge/';
import { DataFormat } from '../../framework/charting/charts/DataFormat';
import { ChartByIdentifierSeries } from '../chartByIdentifier/CharByIdentifierSeries';
import { mapSeries } from '../chartByIdentifier/mapSeries';
import { defaultGaugeChartConfig } from './gaugeChart.definition';
import { errorTextColor, infoTextColor, warningTextColor } from '../../common/constants';
import { ValueTypes } from '../../common';
import { RoundingHelper } from '../../framework/helpers/helpers';
import { IExportToCsvService } from '@services/data';
import { GaugeChartWidget } from './gaugeChart.widget';

const colorDic = {
    0: {
        fill: infoTextColor,
        classed: 'fill-info'
    },
    1: {
        fill: warningTextColor,
        classed: 'fill-warn'
    },
    2: {
        fill: errorTextColor,
        classed: 'fill-error'
    }
};

interface IGaugeVm {
    data: GaugeDataFormat[];
    options: GaugeOptions;
}

class GaugeChartController implements angular.IComponentController {
    gauge: IGaugeVm;
    id: string;
    options: GaugeOptions;
    series: any[] = [];

    readonly boundData: WidgetValues;
    readonly boundSeries: (ChartByIdentifierSeries & IValueViewModel)[] = []; // Is this not a SeriesDefinition<TagDateValue>?
    // Not used
    readonly isBound: boolean;
    readonly item: GaugeChartWidget;
    readonly ratio: number;

    private _seriesOptions: { dataFormat: DataFormat<any, any> };
    private _defaultValue: GaugeDataFormat;

    static $inject = ['exportToCsvService'];

    constructor(private _exportToCsvService: IExportToCsvService) {
        this._seriesOptions = { dataFormat: defaultGaugeChartConfig.dataFormat };
        this._defaultValue = defaultGaugeChartConfig.value;
        this.options = defaultGaugeChartConfig.options;
    }

    $onInit() {
        if (this.item) {
            this.id = `id-${this.item.guid}`;
            this.item.exportToCsv = () => this._exportToCsvService.exportAggregatedToCsv(this.series, this.boundData.aggregatedValue, this.item.name);
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        this.refresh();
    }

    /**
     * Called when series or bound data changes
     */
    private refresh(): void {
        const gauge: IGaugeVm = {
            options: angular.copy(this.options),
            data: []
        };

        // Configure options
        const configOptions = this.item.configuration.options;
        if (configOptions.thickness) {
            gauge.options.thickness = configOptions.thickness / 100;
        }
        gauge.options.isTitleVisible = configOptions.isTitleVisible;
        gauge.options.isLegendVisible = configOptions.isLegendVisible;
        gauge.options.title = configOptions.title;
        gauge.options.showTarget = configOptions.showTarget;

        // map data
        if (this.boundData && this.boundSeries) {
            try {
                this.series = mapSeries(this.boundSeries, [], this.item, this._seriesOptions);
                gauge.data = this.boundSeries.map((s, i) => {
                    const mapped = this.series[i]; //mapSeries([s], [], this.item as any, this._seriesOptions)[0];
                    const gaugeData = this.serieToDataFormat(mapped, this.boundData, this._seriesOptions.dataFormat);
                    return gaugeData;
                });
            } catch (e) {
                console.log('Error when mapping series', e);
            }
        }
        this.gauge = gauge;
    }

    /**
     * Map a value from bounddata to a DataFormat for a given serie
     */
    private serieToDataFormat(mapped: any, data: WidgetValues, df: DataFormat<any, any>): GaugeDataFormat {
        const minMax = this.getMinMax(mapped);
        const result = angular.copy(this._defaultValue);
        const color = this.getSerieColor(mapped);

        result.serie = {
            color: color,
            id: df.identifier(mapped),
            attributes: mapped.attributes,
            click: mapped.clickCallback,
            label: df.label(mapped)
        };

        // find the serie
        const found = _.find(data.aggregatedValue, (av) => av.guid === mapped.guid && av.valueType === mapped.valueType);
        if (found) {
            result.value = found.value;
            result.serie.targetValue = found.target !== null && found.target !== undefined ? found.target : mapped.target;
        } else {
            result.serie.targetValue = mapped.target;
        }

        const values = [];
        if (result.value !== null && result.value !== undefined) {
            values.push(result.value);
        }
        if (result.serie?.targetValue !== null && result.serie?.targetValue !== undefined) {
            values.push(result.serie.targetValue);
        }
        if (values.length) {
            let changedMin = false;
            let changedMax = false;
            if (minMax.min === null || minMax.min === undefined) {
                minMax.min = _.min(values);
                changedMin = true;
            }
            if (minMax.max === null || minMax.max === undefined) {
                minMax.max = _.max(values);
                changedMax = true;
            }
            if (changedMin && changedMax) {
                const rh = new RoundingHelper();
                const rounded = rh.round(minMax.min, minMax.max);
                minMax.min = rounded.min;
                minMax.max = rounded.max;
            }
        }

        result.format = `,.${mapped.decimals ?? 0}f`;
        const defaultBand = { min: minMax.min, max: minMax.max, color: color, classed: 'fill-sucess' };
        result.bands = [defaultBand];
        const { configuration } = this.item;
        if (configuration.options.showAlarms && mapped.alarms) {
            let alarms: Alarm[] = mapped.alarms.filter((a: Alarm) => !a.isDisabled);
            let severities =
                configuration.options.severities && configuration.options.severities.length
                    ? configuration.options.severities.map((s) => AlarmLevel[s])
                    : [AlarmLevel.Information, AlarmLevel.Warning, AlarmLevel.Critical];
            severities = severities.sort((a, b) => (a > b ? 1 : -1));
            alarms = alarms.filter((a) => severities.indexOf(a.level) >= 0);
            if (alarms.length) {
                const globalMin =
                    minMax.min !== null && minMax.min !== undefined
                        ? minMax.min
                        : _.min(alarms.filter((a) => a.low !== null && a.low !== undefined && !a.isManual).map((a) => a.low));
                const globalMax =
                    minMax.max !== null && minMax.max !== undefined
                        ? minMax.max
                        : _.max(alarms.filter((a) => a.high !== null && a.high !== undefined && !a.isManual).map((a) => a.high));
                minMax.min = globalMin;
                minMax.max = globalMax;
                defaultBand.min = minMax.min;
                defaultBand.max = minMax.max;
                const mappedAlarms = this.mapAlarmsToBands(alarms, severities, minMax);
                result.bands = result.bands.concat(mappedAlarms);
            }
        }

        return result;
    }

    private mapAlarmsToBands(alarms: Alarm[], severities: AlarmLevel[], minMax: { min: number; max: number }): any[] {
        const mappedAlarms = [];
        const { min, max } = minMax;

        severities.forEach((s) => {
            const col = colorDic[s];
            const filteredAlarms = alarms.filter((a) => a.level === s);
            filteredAlarms.forEach((alarm) => {
                this.convertRelativeDeviation(alarm);
                // outside bounds
                if (alarm.mode === 0) {
                    if (alarm.low !== null && alarm.low !== undefined) {
                        mappedAlarms.push({
                            min: min,
                            max: alarm.low,
                            color: col.fill,
                            classed: col.classed
                        });
                    }
                    if (alarm.high !== null && alarm.high !== undefined) {
                        mappedAlarms.push({
                            min: alarm.high,
                            max: max,
                            color: col.fill,
                            classed: col.classed
                        });
                    }
                } else if (alarm.mode === 1) {
                    mappedAlarms.push({
                        min: alarm.low,
                        max: alarm.high,
                        color: col.fill,
                        classed: col.classed
                    });
                }
            });
        });

        return mappedAlarms;
    }

    private convertRelativeDeviation(alarm: Alarm): Alarm {
        // To improve someday
        // Apply conversion directly on Identifier.Alarms
        if (alarm.value.toLocaleLowerCase() === ValueTypes.relativeDeviation.key.toLocaleLowerCase()) {
            alarm.high = alarm.high * 100;
            alarm.low = alarm.low * 100;
        }
        return alarm;
    }

    private getSerieColor(serie: ChartByIdentifierSeries): string {
        let result = serie.color;
        if (this.item.configuration?.options?.coloringMode === ColoringMode.SingleColor) {
            result = this.item.configuration.options.color;
        }
        return result;
    }

    private getMinMax(vm: IValueViewModel): { min: number; max: number } {
        const opt = this.item.configuration.options;
        return {
            min: opt.min !== undefined && opt.min !== null ? opt.min : vm.min,
            max: opt.max !== undefined && opt.max !== null ? opt.max : vm.max
        };
    }
}

export const wtGaugeChart: ComponentDefinition = {
    componentId: 'wtGaugeChart',
    controller: GaugeChartController,
    template: require('./gaugeChart.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<',
        boundSeries: '<',
        isBound: '<?',
        item: '<',
        ratio: '<?'
    }
};
